import { supabase } from './supabase'

/**
 * Realtime Manager - Centralized management of Supabase realtime subscriptions
 * Handles connection lifecycle, error handling, and subscription cleanup
 */
export class RealtimeManager {
  constructor() {
    this.subscriptions = new Map()
    this.connectionState = 'DISCONNECTED'
    this.connectionCallbacks = new Set()
    this.reconnectAttempts = new Map() // Per-subscription retry budget, keyed by subscriptionKey
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 1000 // Start with 1 second
    this.currentUserId = null // Track current user for connection reuse
    this.connectionMetadata = this.loadConnectionMetadata()
    this.reconnectionTimers = new Map() // Pending reconnection timers, keyed by subscriptionKey
    this.healthCheckInterval = null // Track health monitoring interval

    // Track disconnect/reconnect timing for refresh manager
    this.lastDisconnectTime = null
    this.lastConnectTime = null
    this.previousConnectionState = 'DISCONNECTED'
  }

  /**
   * Load connection metadata from localStorage
   */
  loadConnectionMetadata() {
    try {
      const stored = localStorage.getItem('realtime_connection_metadata')
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.warn('[RealtimeManager] Failed to load connection metadata:', error)
      return null
    }
  }

  /**
   * Save connection metadata to localStorage
   */
  saveConnectionMetadata(metadata) {
    try {
      localStorage.setItem('realtime_connection_metadata', JSON.stringify({
        ...metadata,
        timestamp: Date.now()
      }))
      this.connectionMetadata = { ...metadata, timestamp: Date.now() }
    } catch (error) {
      console.warn('[RealtimeManager] Failed to save connection metadata:', error)
    }
  }

  /**
   * Clear connection metadata from localStorage
   */
  clearConnectionMetadata() {
    try {
      localStorage.removeItem('realtime_connection_metadata')
      this.connectionMetadata = null
    } catch (error) {
      console.warn('[RealtimeManager] Failed to clear connection metadata:', error)
    }
  }

  /**
   * Check if there's an existing connection that can be reused
   */
  canReuseConnection(userId, forceFresh = false) {
    // If forceFresh is requested, clear any stale state and don't reuse
    if (forceFresh) {
      console.log('[RealtimeManager] Force-fresh initialization requested, clearing stale state')
      this.clearConnectionMetadata()
      return false
    }

    // CRITICAL FIX: If subscriptions Map is empty but localStorage has metadata,
    // this indicates a page reload scenario where the singleton was reinitialized
    // but stale metadata persists. Clear it and force fresh initialization.
    if (this.subscriptions.size === 0 && this.connectionMetadata) {
      console.warn('[RealtimeManager] Detected stale localStorage metadata with no active subscriptions (page reload), forcing fresh initialization')
      this.clearConnectionMetadata()
      return false
    }

    // Check if we have active subscriptions for the same user
    if (this.subscriptions.size > 0 && this.currentUserId === userId) {
      console.log('[RealtimeManager] Reusing active connection with', this.subscriptions.size, 'subscriptions')
      return true
    }

    // Check localStorage metadata
    if (this.connectionMetadata && this.connectionMetadata.userId === userId) {
      const timeSinceLastConnection = Date.now() - this.connectionMetadata.timestamp
      const maxStaleTime = 5 * 60 * 1000 // 5 minutes

      if (timeSinceLastConnection < maxStaleTime) {
        // Double-check we actually have subscriptions before trusting metadata
        if (this.subscriptions.size > 0) {
          console.log('[RealtimeManager] Reusing recent connection within 5-minute window')
          return true
        } else {
          console.warn('[RealtimeManager] Metadata says connection exists but subscriptions Map is empty, clearing stale metadata')
          this.clearConnectionMetadata()
          return false
        }
      } else {
        console.log('[RealtimeManager] Connection metadata expired (>5 minutes), clearing')
        this.clearConnectionMetadata()
      }
    }

    return false
  }

  /**
   * Initialize connection for a user with reuse detection
   */
  initializeForUser(userId, forceFresh = false) {
    if (this.canReuseConnection(userId, forceFresh)) {
      this.currentUserId = userId

      // Update connection metadata
      this.saveConnectionMetadata({
        userId,
        subscriptions: this.getActiveSubscriptions(),
        connectionState: this.connectionState
      })

      return {
        reused: true,
        subscriptions: this.getActiveSubscriptions(),
        connectionState: this.connectionState
      }
    }

    // If we can't reuse, prepare for new connection
    if (this.currentUserId && this.currentUserId !== userId) {
      console.log('[RealtimeManager] Different user detected, resetting state')
      this.resetState()
    }

    this.currentUserId = userId
    this.saveConnectionMetadata({
      userId,
      subscriptions: [],
      connectionState: 'INITIALIZING'
    })

    console.log('[RealtimeManager] Initialized fresh connection for user:', userId)

    return {
      reused: false,
      subscriptions: [],
      connectionState: 'INITIALIZING'
    }
  }

  /**
   * Subscribe to table changes with automatic reconnection
   */
  subscribe(subscriptionKey, config) {
    // Cancel any pending backoff retry for this key: we're establishing a fresh
    // channel now, so a queued retry (e.g. scheduled before a focus/online
    // revive) is obsolete and would otherwise tear this channel back down.
    this.cancelReconnectionTimer(subscriptionKey)

    // Clean up existing subscription if it exists
    if (this.subscriptions.has(subscriptionKey)) {
      this.unsubscribe(subscriptionKey)
    }

    const {
      table,
      event = '*', // INSERT, UPDATE, DELETE, or * for all
      filter = '',
      callback,
      errorCallback
    } = config

    // Create subscription
    let subscription = supabase
      .channel(`${subscriptionKey}_channel`)
      .on(
        'postgres_changes',
        {
          event,
          schema: 'public',
          table,
          filter
        },
        (payload) => {
          try {
            callback(payload)
          } catch (error) {
            console.error(`[RealtimeManager] Error in callback for ${subscriptionKey}:`, error)
            if (errorCallback) errorCallback(error)
          }
        }
      )
      .subscribe((status, error) => {
        if (status === 'SUBSCRIBED') {
          this.connectionState = 'CONNECTED'
          this.reconnectAttempts.set(subscriptionKey, 0) // This channel recovered — reset its budget
          this.notifyConnectionCallbacks(true)
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          this.connectionState = 'ERROR'
          this.notifyConnectionCallbacks(false)
          
          // Check for specific Cloudflare cookie errors
          if (error && (
            error.message?.includes('__cf_bm') || 
            error.message?.includes('cookie') ||
            error.message?.includes('domain')
          )) {
            console.warn(`[RealtimeManager] Cloudflare cookie issue detected for ${subscriptionKey}, implementing longer retry delay`)
            // Wait longer before reconnecting for cookie issues
            setTimeout(() => {
              this.handleReconnection(subscriptionKey, config)
            }, 10000) // 10 second delay for cookie issues
          } else {
            this.handleReconnection(subscriptionKey, config)
          }
        } else if (status === 'CLOSED') {
          this.connectionState = 'DISCONNECTED'
          this.notifyConnectionCallbacks(false)
        }
      })

    this.subscriptions.set(subscriptionKey, {
      subscription,
      config,
      table,
      isActive: true
    })

    // Update connection metadata with new subscription
    if (this.currentUserId) {
      this.saveConnectionMetadata({
        userId: this.currentUserId,
        subscriptions: this.getActiveSubscriptions(),
        connectionState: this.connectionState
      })
    }

    return subscription
  }

  /**
   * Unsubscribe from a specific subscription
   */
  unsubscribe(subscriptionKey) {
    const subscriptionData = this.subscriptions.get(subscriptionKey)
    if (subscriptionData) {
      subscriptionData.subscription.unsubscribe()
      this.subscriptions.delete(subscriptionKey)
    }
  }

  /**
   * Unsubscribe from all subscriptions
   */
  unsubscribeAll() {
    for (const [key] of this.subscriptions) {
      this.unsubscribe(key)
    }
    this.subscriptions.clear()
  }

  /**
   * Whether a disconnect is expected/benign (backgrounded tab or offline).
   * Browsers throttle WebSockets on hidden tabs, so these drops are routine and
   * must NOT be logged at warn/error (both are forwarded to OpenObserve). Dead
   * channels are revived via reviveConnections() when the tab/network returns.
   */
  isExpectedDisconnect() {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return true
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
    return false
  }

  /**
   * Handle automatic reconnection with per-subscription exponential backoff and
   * Cloudflare error handling. Each subscription has its own retry budget, so a
   * simultaneous mass drop (e.g. a throttled backgrounded tab dropping every
   * channel at once) can't exhaust a shared counter and abandon channels.
   */
  handleReconnection(subscriptionKey, config) {
    const attempts = this.reconnectAttempts.get(subscriptionKey) || 0

    if (attempts >= this.maxReconnectAttempts) {
      // Budget exhausted for this channel. It stays revivable: reviveConnections()
      // re-establishes it on the next focus/online event.
      this.connectionState = 'ERROR'
      this.notifyConnectionCallbacks(false)

      if (this.isExpectedDisconnect()) {
        // Predictable (hidden tab / offline) — keep this out of telemetry entirely.
        console.log(`[RealtimeManager] Reconnection deferred for ${subscriptionKey} (tab hidden or offline); will revive when active again`)
      } else {
        // Foreground + online: realtime is genuinely failing while the user is
        // active — worth a warning, but recoverable, so not an error.
        console.warn(`[RealtimeManager] Max reconnection attempts reached for ${subscriptionKey} while active; will retry on next focus/online event`)
      }
      return
    }

    const nextAttempt = attempts + 1
    this.reconnectAttempts.set(subscriptionKey, nextAttempt)
    const delay = this.reconnectDelay * Math.pow(2, nextAttempt - 1)

    // Add additional delay for Cloudflare cookie issues
    const cloudflareDelay = nextAttempt > 2 ? delay + 5000 : delay
    
    // Track timer to prevent accumulation
    if (!this.reconnectionTimers) {
      this.reconnectionTimers = new Map()
    }

    // Only one pending retry per key; supersede any earlier one.
    this.cancelReconnectionTimer(subscriptionKey)

    const timerId = setTimeout(() => {
      // Remove this timer from tracking before re-subscribing
      this.reconnectionTimers.delete(subscriptionKey)

      if (this.subscriptions.has(subscriptionKey)) {
        // Clear any existing subscription before retry
        const existingSubscription = this.subscriptions.get(subscriptionKey)
        if (existingSubscription?.subscription) {
          try {
            existingSubscription.subscription.unsubscribe()
          } catch (error) {
            console.warn(`[RealtimeManager] Error cleaning up subscription for retry:`, error)
          }
        }

        this.subscribe(subscriptionKey, config)
      }
    }, cloudflareDelay)

    // Track this timer by key
    this.reconnectionTimers.set(subscriptionKey, timerId)
  }

  /**
   * Cancel a single subscription's pending backoff retry timer, if any.
   */
  cancelReconnectionTimer(subscriptionKey) {
    const timerId = this.reconnectionTimers?.get(subscriptionKey)
    if (timerId !== undefined) {
      clearTimeout(timerId)
      this.reconnectionTimers.delete(subscriptionKey)
    }
  }

  /**
   * Add callback for connection state changes
   */
  onConnectionStateChange(callback) {
    this.connectionCallbacks.add(callback)
    
    // Return unsubscribe function
    return () => {
      this.connectionCallbacks.delete(callback)
    }
  }

  /**
   * Notify all connection callbacks with connection state and metadata
   */
  notifyConnectionCallbacks(isConnected) {
    // Track state transitions for disconnect duration
    const now = Date.now()
    const wasDisconnected = this.previousConnectionState === 'DISCONNECTED' ||
                           this.previousConnectionState === 'ERROR'

    // Calculate disconnect duration
    let disconnectDuration = null
    if (isConnected && wasDisconnected && this.lastDisconnectTime) {
      disconnectDuration = now - this.lastDisconnectTime
      this.lastConnectTime = now
      console.log(`[RealtimeManager] Reconnected after ${(disconnectDuration / 1000).toFixed(1)}s`)
    } else if (!isConnected && !wasDisconnected) {
      // Only set disconnect time on initial disconnect (not on subsequent error transitions)
      // This preserves the original disconnect timestamp for accurate duration calculation
      this.lastDisconnectTime = now
      console.log('[RealtimeManager] Disconnected from realtime')
    }

    // Build metadata for callbacks
    const metadata = {
      wasDisconnected,
      disconnectDuration,
      previousState: this.previousConnectionState,
      currentState: this.connectionState,
      timestamp: now
    }

    // Update previous state
    this.previousConnectionState = this.connectionState

    // Notify all callbacks with metadata
    for (const callback of this.connectionCallbacks) {
      try {
        callback(isConnected, metadata)
      } catch (error) {
        console.error('[RealtimeManager] Error in connection callback:', error)
      }
    }
  }

  /**
   * Get current connection state
   */
  getConnectionState() {
    return this.connectionState
  }

  /**
   * Check if subscription exists and is active
   */
  hasActiveSubscription(subscriptionKey) {
    const subscription = this.subscriptions.get(subscriptionKey)
    return subscription?.isActive || false
  }

  /**
   * Get list of active subscription keys
   */
  getActiveSubscriptions() {
    return Array.from(this.subscriptions.keys()).filter(key => 
      this.subscriptions.get(key)?.isActive
    )
  }

  /**
   * Pause a subscription (useful for offline mode)
   */
  pauseSubscription(subscriptionKey) {
    const subscriptionData = this.subscriptions.get(subscriptionKey)
    if (subscriptionData) {
      subscriptionData.isActive = false
      console.log(`[RealtimeManager] Paused subscription: ${subscriptionKey}`)
    }
  }

  /**
   * Resume a paused subscription
   */
  resumeSubscription(subscriptionKey) {
    const subscriptionData = this.subscriptions.get(subscriptionKey)
    if (subscriptionData && !subscriptionData.isActive) {
      subscriptionData.isActive = true
      this.subscribe(subscriptionKey, subscriptionData.config)
      console.log(`[RealtimeManager] Resumed subscription: ${subscriptionKey}`)
    }
  }

  /**
   * Pause all subscriptions (useful for offline mode)
   */
  pauseAll() {
    console.log('[RealtimeManager] Pausing all subscriptions')
    for (const [key] of this.subscriptions) {
      this.pauseSubscription(key)
    }
  }

  /**
   * Resume all paused subscriptions
   */
  resumeAll() {
    console.log('[RealtimeManager] Resuming all subscriptions')
    for (const [key] of this.subscriptions) {
      this.resumeSubscription(key)
    }
  }

  /**
   * Re-establish any dead channels after the tab regains focus or the network
   * comes back. A backgrounded tab's sockets get throttled/killed and may have
   * exhausted their retry budget; this gives every dead channel a fresh budget
   * and re-subscribes it, so a returning user gets live updates without a page
   * reload.
   *
   * Channels that are 'joined' (healthy) or 'joining' (a subscribe already in
   * flight) are left untouched: an in-flight join resolves on its own — to
   * 'joined' (budget resets) or an error (which routes back through
   * handleReconnection). Skipping 'joining' also avoids doubling the work when
   * this runs right after resumeAll() in the online handler, where the freshly
   * resumed channels are still mid-join.
   */
  reviveConnections() {
    if (this.subscriptions.size === 0) return

    // Snapshot: subscribe() mutates the Map (unsubscribe + re-add) as we go.
    const entries = Array.from(this.subscriptions.entries())
    for (const [key, subscriptionData] of entries) {
      if (!subscriptionData.isActive) continue

      const channelState = subscriptionData.subscription?.state
      if (channelState !== 'joined' && channelState !== 'joining') {
        console.log(`[RealtimeManager] Reviving subscription after focus/online: ${key} (state: ${channelState || 'undefined'})`)
        this.reconnectAttempts.set(key, 0) // Fresh budget for the revived channel
        this.subscribe(key, subscriptionData.config)
      }
    }
  }

  /**
   * Check if manager has stale state (subscriptions exist but Redux thinks it's not initialized)
   */
  hasStaleState() {
    return this.subscriptions.size > 0 || this.connectionState !== 'DISCONNECTED'
  }

  /**
   * Health check for existing connections with actual Supabase channel validation
   */
  validateConnectionHealth() {
    // CRITICAL FIX: Explicitly check for empty subscriptions first
    if (this.subscriptions.size === 0) {
      console.warn('[RealtimeManager] Health check failed: No active subscriptions')
      this.connectionState = 'DISCONNECTED'
      return false
    }

    let healthyConnections = 0
    const totalConnections = this.subscriptions.size
    const unhealthyChannels = []

    for (const [key, subscriptionData] of this.subscriptions) {
      if (subscriptionData.isActive && subscriptionData.subscription) {
        // Enhanced health check - verify actual Supabase channel state
        const channel = subscriptionData.subscription
        const channelState = channel?.state

        // CRITICAL FIX: Only count as healthy if state is explicitly 'joined'
        // Don't trust undefined or other states
        if (channelState === 'joined') {
          healthyConnections++
          console.log(`[RealtimeManager] Healthy subscription: ${key} (state: ${channelState})`)
        } else {
          console.warn(`[RealtimeManager] Unhealthy subscription: ${key} (state: ${channelState || 'undefined'})`)
          unhealthyChannels.push({ key, state: channelState || 'undefined' })
        }
      } else {
        console.warn(`[RealtimeManager] Inactive or missing subscription: ${key}`)
        unhealthyChannels.push({ key, state: 'inactive' })
      }
    }

    const isHealthy = healthyConnections === totalConnections && totalConnections > 0

    // Update connection state based on actual health
    if (isHealthy) {
      this.connectionState = 'CONNECTED'
      console.log(`[RealtimeManager] Health check passed: ${healthyConnections}/${totalConnections} subscriptions healthy`)
    } else if (healthyConnections > 0) {
      this.connectionState = 'ERROR' // Partial connection
      console.warn(`[RealtimeManager] Health check partial: ${healthyConnections}/${totalConnections} healthy, unhealthy channels:`, unhealthyChannels)
    } else {
      this.connectionState = 'DISCONNECTED'
      console.error(`[RealtimeManager] Health check failed: 0/${totalConnections} healthy, all channels unhealthy:`, unhealthyChannels)
    }

    return isHealthy
  }

  /**
   * Start periodic health monitoring
   */
  startHealthMonitoring(interval = 30000) { // 30 seconds
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
    }

    this.healthCheckInterval = setInterval(() => {
      if (this.currentUserId && this.subscriptions.size > 0) {
        const isHealthy = this.validateConnectionHealth()
        
        if (!isHealthy && this.connectionState !== 'ERROR') {
          console.log('[RealtimeManager] Health check failed, connection may be stale')
          // Update metadata to reflect potential issues
          this.saveConnectionMetadata({
            userId: this.currentUserId,
            subscriptions: this.getActiveSubscriptions(),
            connectionState: 'UNHEALTHY',
            lastHealthCheck: Date.now()
          })
        }
      }
    }, interval)

    console.log('[RealtimeManager] Started health monitoring')
  }

  /**
   * Stop health monitoring
   */
  stopHealthMonitoring() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
      this.healthCheckInterval = null
      console.log('[RealtimeManager] Stopped health monitoring')
    }
  }

  /**
   * Enhanced reset that includes health monitoring cleanup
   */
  resetState() {
    console.log('[RealtimeManager] Resetting all state')

    // CRITICAL FIX: Clean up connection listener BEFORE clearing callbacks
    // This prevents the unsubscriber from trying to delete from an already-cleared Set
    if (this.connectionUnsubscriber) {
      try {
        this.connectionUnsubscriber()
      } catch (error) {
        console.warn('[RealtimeManager] Error unsubscribing connection listener:', error)
      }
      this.connectionUnsubscriber = null
    }

    // Stop health monitoring
    this.stopHealthMonitoring()

    // Clean up any pending reconnection timers
    this.clearReconnectionTimers()

    // Clean up any existing subscriptions
    this.unsubscribeAll()

    // Reset all internal state (NOW safe to clear callbacks)
    this.subscriptions.clear()
    this.connectionState = 'DISCONNECTED'
    this.connectionCallbacks.clear()
    this.reconnectAttempts.clear()
    this.currentUserId = null

    // Reset timing tracking
    this.lastDisconnectTime = null
    this.lastConnectTime = null
    this.previousConnectionState = 'DISCONNECTED'

    // Clear connection metadata
    this.clearConnectionMetadata()
  }

  /**
   * Clear any pending reconnection timers to prevent accumulation
   */
  clearReconnectionTimers() {
    if (this.reconnectionTimers) {
      this.reconnectionTimers.forEach(timerId => {
        clearTimeout(timerId)
      })
      this.reconnectionTimers.clear()
    } else {
      this.reconnectionTimers = new Map()
    }
  }
}

// Create singleton instance
export const realtimeManager = new RealtimeManager()

// Online/Offline detection and cleanup handlers
if (typeof window !== 'undefined') {
  // Track pending resume timer to prevent multiple simultaneous resumes
  let resumeTimer = null

  // Network state handlers
  window.addEventListener('online', () => {
    console.log('[RealtimeManager] Network online event received, waiting for network to stabilize...')

    // Clear any pending resume timer
    if (resumeTimer) {
      clearTimeout(resumeTimer)
    }

    // Wait 10 seconds for network to stabilize before resuming
    // navigator.onLine fires immediately on wake, before TCP/DNS is ready
    // 10s is needed for overnight sleep scenarios where network stack takes longer
    resumeTimer = setTimeout(() => {
      resumeTimer = null
      console.log('[RealtimeManager] Network stabilization delay complete, resuming subscriptions')
      realtimeManager.resumeAll() // Re-subscribe channels paused by the offline handler
      realtimeManager.reviveConnections() // Re-establish channels that died/exhausted retries while offline
    }, 10000)
  })

  window.addEventListener('offline', () => {
    console.log('[RealtimeManager] Network offline, pausing subscriptions')

    // Clear any pending resume timer since we're going offline
    if (resumeTimer) {
      clearTimeout(resumeTimer)
      resumeTimer = null
    }

    realtimeManager.pauseAll()
  })

  // Note: Removed beforeunload cleanup to allow connections to persist across page refreshes
  // This prevents the timing issues where disconnection happens too late during page unload

  // Note: Tab visibility PAUSING was removed to maintain continuous realtime updates.
  // Idle WebSocket connections use negligible resources, and modern browsers already
  // throttle background tabs. Pausing on tab switch created UX issues where subscriptions
  // failed to resume properly, requiring page refreshes.
  //
  // We do, however, REVIVE on return-to-foreground: browsers frequently kill throttled
  // sockets on hidden tabs, which can exhaust a channel's retry budget. Reviving here
  // re-establishes any dead channel when the tab becomes visible again — the common
  // "tab was backgrounded, come back to stale data" case that online/offline misses.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      realtimeManager.reviveConnections()
    }
  })
}

export default realtimeManager
